// local
// models
import { CYLINDER_STATE } from 'salvus-shared/consts/cylinder';
import { PERIOD_TYPE } from 'salvus-shared/consts/patient';
// lib
import { toSearchableString, mapById, sortString, sortNumber } from 'app/lib/helper';

// Helper fuction to sort the flow
const flowToSort = (flow, prescription) => {
    if (flow === '-') {
        return 0;
    }
    const percent = (prescription * 15) / 100;
    return flow > prescription - percent && flow < prescription + percent ? 1 : 2;
};

const SORT = (a, b) => {
    // let result = sortNumber(b.inAlert, a.inAlert);
    // if (result !== 0) {
    //     return result;
    // }
    let result = sortNumber(b.alertBattery, a.alertBattery);
    if (result !== 0) {
        return result;
    }
    result = sortNumber(b.isOffline, a.isOffline);
    if (result !== 0) {
        return result;
    }
    result = sortNumber(b.alertEndTime, a.alertEndTime);
    if (result !== 0) {
        return result;
    }
    result = sortNumber(b.alertPress, a.alertPress);
    if (result !== 0) {
        return result;
    }
    result = sortNumber(b.lowBattery, a.lowBattery);
    if (result !== 0) {
        return result;
    }
    result = sortNumber(b.warningOff, a.warningOff);
    if (result !== 0) {
        return result;
    }
    result = sortNumber(b.warningEndTime, a.warningEndTime);
    if (result !== 0) {
        return result;
    }
    const aFlow = flowToSort(a.flow, a.prescription);
    const bFlow = flowToSort(b.flow, b.prescription);
    result = sortNumber(bFlow, aFlow);
    if (result !== 0) {
        return result;
    }
    return sortString(a.name, b.name);
};

const filterByTerm = (item, term) => toSearchableString(item.name).includes(toSearchableString(term));

const filterByType = (item, filters) => filters.includes(item.periodType);

const filterData = (data, term, filters) => {
    data = data.filter(item => {
        return filterByTerm(item, term) && filterByType(item, filters);
    });
    data.sort(SORT);
    return data;
};

const updateRepositionAndCylinder = function(data, patient, cylinder, repositionToAdd, newState) {
    return mapById(data, patient.id, pItem => {
        return {
            ...pItem,
            reposition: pItem.reposition + repositionToAdd,
            cylindersList: mapById(pItem.cylindersList, cylinder.id, cItem => {
                return {
                    ...cItem,
                    state: newState,
                };
            }),
        };
    });
};

const calcCylinderUpdates = function(data) {
    let cylinderUpdates = 0;
    data.forEach(patient => {
        patient.cylindersList.forEach(cylinder => {
            if (cylinder.state === CYLINDER_STATE.PENDING) {
                cylinderUpdates++;
            }
        });
    });
    return cylinderUpdates;
};

const initialState = () => ({
    cylinderUpdates: 0,
    autoUpdate: true,
    view: 'list',
    mapParams: {
        center: { lat: -8.05489, lng: -34.924207 },
        zoom: 12,
    },
    loading: false,
    data: [],
    filter: {
        term: '',
        type: [PERIOD_TYPE.IF_NECESSARY, PERIOD_TYPE.CONTINUOUS_USE],
        data: [],
    },
});

const ACTION_KEY = 'MONITOR';

const reducer = (state = initialState(), action) => {
    switch (action.type) {
        case `${ACTION_KEY}_PENDING`: {
            return {
                ...state,
                loading: true,
            };
        }
        case `${ACTION_KEY}_REJECTED`: {
            return {
                ...state,
                loading: false,
            };
        }
        case `${ACTION_KEY}_FULFILLED`: {
            const data = action.payload;
            const cylinderUpdates = calcCylinderUpdates(data);
            return {
                ...state,
                loading: false,
                data,
                cylinderUpdates,
                filter: {
                    ...state.filter,
                    data: filterData(data, state.filter.term, state.filter.type),
                },
            };
        }
        case `${ACTION_KEY}_UPDATE_AUTO_UPDATE`: {
            return {
                ...state,
                autoUpdate: action.payload,
            };
        }
        case `${ACTION_KEY}_UPDATE_VIEW`: {
            return {
                ...state,
                view: action.payload,
            };
        }
        case `${ACTION_KEY}_UPDATE_MAP_PARAMS`: {
            return {
                ...state,
                mapParams: action.payload,
            };
        }
        case `${ACTION_KEY}_RESET_MAP_PARAMS`: {
            return {
                ...state,
                mapParams: {
                    center: { lat: -8.05489, lng: -34.924207 },
                    zoom: 12,
                },
            };
        }
        case `${ACTION_KEY}_SAVE_PENDING_FULFILLED`: {
            return {
                ...state,
                cylinderUpdates: 0,
                autoUpdate: true,
                loading: false,
            };
        }
        case `${ACTION_KEY}_UPDATE_PRESCRIPTION`: {
            const { patientId, newPrescription } = action.payload;
            const data = mapById(state.data, patientId, pItem => {
                return {
                    ...pItem,
                    prescription: newPrescription,
                };
            });
            return {
                ...state,
                data,
                filter: {
                    ...state.filter,
                    data: filterData(data, state.filter.term, state.filter.type),
                },
            };
        }
        case `${ACTION_KEY}_ADD_RESTITUTION`: {
            const patientId = action.payload;
            const patient = state.filter.data.find(p => p.id === patientId);
            const cylinder = patient.cylindersList.find(c => c.state === CYLINDER_STATE.EMPTY);
            if (!cylinder) {
                return state;
            }
            const filterData = updateRepositionAndCylinder(state.filter.data, patient, cylinder, 1, CYLINDER_STATE.PENDING);
            const data = updateRepositionAndCylinder(state.data, patient, cylinder, 1, CYLINDER_STATE.PENDING);
            const cylinderUpdates = calcCylinderUpdates(data);
            return {
                ...state,
                data,
                cylinderUpdates,
                filter: {
                    ...state.filter,
                    data: filterData,
                },
            };
        }
        case `${ACTION_KEY}_REMOVE_RESTITUTION`: {
            const patientId = action.payload;
            const patient = state.filter.data.find(p => p.id === patientId);
            const cylinder = patient.cylindersList
                .slice()
                .reverse()
                .find(c => c.state === CYLINDER_STATE.PENDING);
            if (!cylinder) {
                return state;
            }
            const filterData = updateRepositionAndCylinder(state.filter.data, patient, cylinder, -1, CYLINDER_STATE.EMPTY);
            const data = updateRepositionAndCylinder(state.data, patient, cylinder, -1, CYLINDER_STATE.EMPTY);
            const cylinderUpdates = calcCylinderUpdates(data);
            return {
                ...state,
                data,
                cylinderUpdates,
                filter: {
                    ...state.filter,
                    data: filterData,
                },
            };
        }
        // case `${ACTION_KEY}_UPDATE_RESTITUTION`: {
        //     let cylinderUpdates = 0;
        //     state.filter.data.forEach(patient => {
        //         patient.cylindersList.forEach(cylinder => {
        //             if (cylinder.state === CYLINDER_STATE.PENDING) {
        //                 cylinderUpdates++;
        //             }
        //         });
        //     });
        //     return {
        //         ...state,
        //         cylinderUpdates,
        //     };
        // }
        case `${ACTION_KEY}_SEARCH`: {
            const term = action.payload;
            const data = filterData(state.data, term, state.filter.type);
            return {
                ...state,
                autoUpdate: true,
                filter: {
                    ...state.filter,
                    term,
                    data,
                },
            };
        }
        case `${ACTION_KEY}_FILTER`: {
            const type = action.payload;
            const data = filterData(state.data, state.filter.term, type);
            return {
                ...state,
                autoUpdate: true,
                filter: {
                    ...state.filter,
                    type,
                    data,
                },
            };
        }
        case `${ACTION_KEY}_RESET`: {
            return { ...initialState() };
        }
        default:
            return state;
    }
};

export default reducer;
